const selectors = {
  popupClose: ".js-popup-close",
  spinner: ".js-spinner",
  initCalculatorsBtn: ".js-init-calculators",
  calculatorsPopup: ".js-calculators-popup",
  calculatorOverlay: ".js-calculator-overlay",
  loanTable: "[data-loan-table]",
  productContainer: '[data-section-type="product"]',
};

//TBI has no schemes below this amount (price in cents)
const minCalculatorPrice = 10000;

//Elements are looked up when needed instead of cached - each provider button
//is optional (block settings) and the theme editor re-renders the section,
//replacing the buttons and the popup.
const getElement = (selector) => document.querySelector(selector);

const strings = window.calculatorStrings || {};
const logos = window.calculatorLogos || {};

//An error carrying a message that is safe to show to the customer
const calculatorError = (message) => {
  const error = new Error(message || "Calculator error");
  error.customerMessage = message;
  return error;
};

/**
 * Normalized loan entry shared by all providers
 *
 * @typedef {Object} LoanEntry
 * @property {number} months
 * @property {number} monthlyPayment - in euros
 * @property {number} totalAmountDue - in euros
 * @property {number} glp - percent
 * @property {number} gpr - percent
 */

//TBI SCHEMES - the endpoint takes and returns amounts in euros
const getTbiEntries = (priceInCents) =>
  fetch(
    `https://apps.gloross.com/TBIShopifyWebhook/GetCalcForAllScheme?privateAppId=32&amount=${(priceInCents / 100).toFixed(2)}&categoryId=123`,
    {
      method: "POST",
      mode: "cors",
      headers: {
        accept: "application/json",
      },
    },
  )
    .then((response) => response.json())
    .then((data) => {
      // The endpoint answers with HTTP 200 even when it fails - `message` is
      // then a plain error string instead of a JSON payload, so `success` has
      // to be checked before parsing.
      if (!data.success) {
        throw new Error(`TBI calculator rejected the request: ${data.message}`);
      }

      return Object.values(JSON.parse(data.message)).map((entry) => ({
        months: Number(entry.NumberInstallments),
        monthlyPayment: Number(entry.MonthlyPayment),
        totalAmountDue: Number(entry.TotalAmountDue),
        glp: Number(entry.Glp),
        gpr: Number(entry.Gpr),
      }));
    });

//Unicredit returns every duration from 3 to 36 months - only these are shown
const unicreditMonths = [3, 6, 9, 12, 15, 18, 24];

//UNICREDIT SCHEMES - the endpoint takes and returns amounts in euros. It only
//accepts GET and authenticates the store by the X-Shop-Domain header.
const getUnicreditEntries = (priceInCents) =>
  fetch(
    `https://matraciapps.gloross.com/api/ucfin/monthly-prices?price=${(priceInCents / 100).toFixed(2)}`,
    {
      mode: "cors",
      headers: {
        accept: "application/json",
        "X-Shop-Domain": "matraci-bg1.myshopify.com",
      },
    },
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(
          `Unicredit calculator responded with status ${response.status}`,
        );
      }

      return response.json();
    })
    .then((data) => {
      if (!data.isSuccess) {
        throw new Error(
          `Unicredit calculator rejected the request: ${data.message}`,
        );
      }

      return (data.data || [])
        .filter((entry) => unicreditMonths.includes(Number(entry.months)))
        .map((entry) => ({
          months: Number(entry.months),
          monthlyPayment: Number(entry.monthlyPayment),
          totalAmountDue: Number(entry.totalPayment),
          glp: Number(entry.interestPercent),
          gpr: calculateUnicreditGpr(Number(entry.interestPercent)),
        }));
    });

//Unicredit only returns the GLP, so the GPR is derived from it
const calculateUnicreditGpr = (glp) => (Math.pow(1 + glp / 1200, 12) - 1) * 100;

const providers = {
  tbi: { name: "TBI", getEntries: getTbiEntries },
  unicredit: { name: "UniCredit", getEntries: getUnicreditEntries },
};

/**
 * When a promo is active the same duration comes twice - the regular scheme
 * and the promo one. Keep a single entry per duration, preferring the
 * cheaper one (lower GLP, then lower GPR).
 *
 * The kept entry is flagged as a promo when it won over a duplicate or is
 * interest free - TBI sends its 0% promo without a regular duplicate.
 *
 * @param {Array<LoanEntry>} entries
 * @returns {Array<LoanEntry & {isPromo: boolean}>} - one entry per duration, sorted by months
 */
const pickEntryPerDuration = (entries) => {
  const byMonths = new Map();
  const duplicatedMonths = new Set();

  entries.forEach((entry) => {
    const current = byMonths.get(entry.months);

    if (current) {
      duplicatedMonths.add(entry.months);
    }

    const isCheaper =
      !current ||
      entry.glp < current.glp ||
      (entry.glp === current.glp && entry.gpr < current.gpr);

    if (isCheaper) {
      byMonths.set(entry.months, entry);
    }
  });

  return [...byMonths.values()]
    .map((entry) => ({
      ...entry,
      isPromo: duplicatedMonths.has(entry.months) || entry.glp === 0,
    }))
    .sort((a, b) => a.months - b.months);
};

const formatPrice = (euros) =>
  window.theme.formatMoney(Math.round(euros * 100), window.theme.moneyFormat);

const formatPercent = (value) => `${value.toFixed(2)}%`;

const formatMaturity = (maturity) =>
  (strings.months || "{{ count }}").replace("{{ count }}", maturity);

const promoClass = (entry) => (entry.isPromo ? ' class="is-promo"' : "");

const buildLoanTable = (provider, entries) => {
  const row = (label, format) => `
    <tr>
      <th scope="row">${label}</th>
      ${entries.map((entry) => `<td${promoClass(entry)}>${format(entry)}</td>`).join("")}
    </tr>
  `;

  return `
    <div class="loan-table">
      <table>
        <thead>
          <tr>
            <th class="loan-table__logo">
              <img src="${logos[provider]}" alt="${providers[provider].name}">
            </th>
            ${entries
              .map(
                (entry) => `
                  <th scope="col"${promoClass(entry)}>
                    ${entry.isPromo ? `<span class="loan-table__badge">${strings.promo}</span>` : ""}
                    ${formatMaturity(entry.months)}
                  </th>
                `,
              )
              .join("")}
          </tr>
        </thead>

        <tbody>
          ${row(strings.monthly, (entry) => formatPrice(entry.monthlyPayment))}
          ${row(strings.glp, (entry) => formatPercent(entry.glp))}
          ${row(strings.gpr, (entry) => formatPercent(entry.gpr))}
          ${row(strings.totalSum, (entry) => formatPrice(entry.totalAmountDue))}
        </tbody>
      </table>
    </div>
  `;
};

const openCalculatorPopup = () => {
  const calculatorsPopup = getElement(selectors.calculatorsPopup);

  calculatorsPopup.classList.add("is-visible");
  calculatorsPopup.previousElementSibling.classList.add("is-visible");
};

const showCalculatorMessage = (message) => {
  getElement(selectors.loanTable).innerHTML =
    `<p class="calculator-popup__message">${message}</p>`;
};

const handleCalculatorClick = (button) => {
  const priceInCents = Number(button.dataset.price);
  const provider = button.dataset.provider;
  const loanTable = getElement(selectors.loanTable);

  if (!Number.isFinite(priceInCents) || priceInCents <= 0) {
    console.error(
      "Calculator: missing or invalid data-price",
      button.dataset.price,
    );
    return;
  }

  if (!providers[provider]) {
    console.error("Calculator: unknown data-provider", provider);
    return;
  }

  button.setAttribute("disabled", true);
  toggleSpinner(true);
  loanTable.innerHTML = "";

  providers[provider]
    .getEntries(priceInCents)
    .then((entries) => {
      const loanEntries = pickEntryPerDuration(
        entries.filter((entry) => Number.isFinite(entry.months)),
      );

      // A valid response with no schemes at all - happens for amounts above
      // the highest scheme limit.
      if (loanEntries.length === 0) {
        throw calculatorError(strings.noSchemes);
      }

      loanTable.innerHTML = buildLoanTable(provider, loanEntries);
      openCalculatorPopup();
    })
    .catch((error) => {
      console.error("Error:", error);
      showCalculatorMessage(error.customerMessage || strings.error);
      openCalculatorPopup();
    })
    .finally(() => {
      toggleSpinner(false);
      button.removeAttribute("disabled");
    });
};

//KEEP THE PRICE IN SYNC WITH THE SELECTED VARIANT
const handleVariantChange = (e) => {
  const productContainer = e.target.closest(selectors.productContainer);
  const buttons = productContainer?.querySelectorAll(
    selectors.initCalculatorsBtn,
  );

  if (!buttons?.length) {
    return;
  }

  const variant = e.detail?.variant;

  if (!variant) {
    buttons.forEach((button) => button.classList.add("is-hidden"));
    return;
  }

  //the same promo discount the template applies to the rendered price
  const discount = Number(productContainer.dataset.discount) || 0;
  const variantPrice = Math.round((variant.price * (100 - discount)) / 100);

  buttons.forEach((button) => {
    button.dataset.price = variantPrice;
    button.classList.toggle("is-hidden", variantPrice < minCalculatorPrice);
  });
};

//TOGGLE SPINNER
const toggleSpinner = (isVisible) => {
  getElement(selectors.spinner)?.classList.toggle("is-visible", isVisible);
};

//CLOSE POPUP
const handlePopupClose = (e) => {
  e?.preventDefault();

  const calculatorsPopup = getElement(selectors.calculatorsPopup);

  calculatorsPopup.classList.remove("is-visible");
  calculatorsPopup.previousElementSibling.classList.remove("is-visible");

  getElement(selectors.loanTable).innerHTML = "";
};

document.addEventListener("click", (e) => {
  const button = e.target.closest(selectors.initCalculatorsBtn);

  if (button) {
    handleCalculatorClick(button);
    return;
  }

  if (
    e.target.closest(`${selectors.popupClose}, ${selectors.calculatorOverlay}`)
  ) {
    handlePopupClose(e);
  }
});

document.addEventListener("theme:variant:change", handleVariantChange);
